import { Avatar, Button } from "@mui/material";
import type { RefObject } from "react";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import type { DesignerBioImages, MatchRecord } from "../types";
import {
  formatDistance,
  getCounterpartReaction,
  getCounterpartReactionBadge,
  getDesignerBioLookupId,
  getHeroImage,
  getMatchDisplayId,
  getProfileImage,
  getProfileSnapshot,
  shouldShowSelectPerfectMatch,
} from "../utils";
import { MatchStatusPill } from "./MatchStatusPill";
import { MatchScoreRing } from "./MatchScoreRing";

export const MatchesMobileCarousel = ({
  matches,
  mobileIndex,
  mobileCarouselRef,
  onScroll,
  onSelectMatch,
  isHomeowner,
  hasActiveMatch,
  selectingMatchId,
  onSelectPerfectMatch,
  isMatchUnavailable,
  bioImageCache,
  userAccountType,
  endOfListMessage,
  hasNextPage,
}: {
  matches: MatchRecord[];
  mobileIndex: number;
  mobileCarouselRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  onSelectMatch: (match: MatchRecord) => void;
  isHomeowner: boolean;
  hasActiveMatch: boolean;
  selectingMatchId: string | null;
  onSelectPerfectMatch: (match: MatchRecord) => void;
  isMatchUnavailable: (match: MatchRecord) => boolean;
  bioImageCache: Record<string, DesignerBioImages>;
  userAccountType?: string | null;
  endOfListMessage: string | null;
  hasNextPage: boolean;
}) => {
  if (!matches.length) {
    return <div className="matches-empty-filter">No matches for this filter.</div>;
  }

  return (
    <div className="matches-mobile-carousel-wrapper">
      <div className="matches-mobile-counter">
        {Math.min(matches.length, mobileIndex + 1)} of {matches.length}
      </div>
      <div
        className="matches-mobile-carousel"
        ref={mobileCarouselRef}
        onScroll={onScroll}
      >
        {matches.map((match) => {
          const isUnavailable = isMatchUnavailable(match);
          const snapshot = getProfileSnapshot(match);
          const profileId = getDesignerBioLookupId(match);
          const profileImages = profileId ? bioImageCache[profileId] : null;
          const profileImage = getProfileImage(match, profileImages, null);
          const heroImage = getHeroImage(
            match,
            profileImages,
            null,
            profileImage,
            userAccountType ?? null
          );
          const topTags =
            match.topMatchTags ||
            ((snapshot?.topMatchTags as string[] | undefined) ?? []);
          const counterpartReactionBadge = getCounterpartReactionBadge(
            getCounterpartReaction(match)
          );
          const showMatchMade = Boolean(match.isMatchMade);
          const showSelectPerfectMatch = shouldShowSelectPerfectMatch({
            match,
            isHomeowner,
            hasActiveMatch,
          });
          const isSelecting = selectingMatchId === match.matchId;
          const displayMatchId = getMatchDisplayId(match.matchId, userAccountType ?? null);

          return (
            <div key={match.matchId} className="matches-mobile-slide">
              <div
                className={`matches-mobile-card ${
                  isUnavailable ? "is-unavailable" : ""
                }`}
                onClick={() => onSelectMatch(match)}
                role="button"
                tabIndex={isUnavailable ? -1 : 0}
                aria-disabled={isUnavailable}
                onKeyDown={(event) => {
                  if (isUnavailable) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectMatch(match);
                  }
                }}
              >
                {heroImage ? (
                  <img
                    src={heroImage}
                    alt={`${displayMatchId} cover`}
                    className="matches-mobile-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="matches-mobile-cover matches-mobile-cover-placeholder" />
                )}
                <div className="matches-mobile-overlay">
                  <div className="matches-mobile-overlay-row">
                    <div className="matches-mobile-user">
                      <div className="matches-mobile-avatar">
                        <Avatar src={profileImage || undefined}>
                          {(match.name ?? displayMatchId).charAt(0)}
                        </Avatar>
                        <span
                          className={`status-dot ${
                            match.isOnline ? "online" : "offline"
                          }`}
                        />
                      </div>
                      <div className="matches-mobile-user-info">
                        <span className="matches-mobile-user-name">{displayMatchId}</span>
                        <span className="matches-mobile-user-meta">
                          <RouteOutlinedIcon />
                          {formatDistance(match.distanceMiles)}
                        </span>
                        {counterpartReactionBadge && (
                          <MatchStatusPill
                            tone={counterpartReactionBadge.tone}
                            label={counterpartReactionBadge.label}
                            className="matches-mobile-counterpart-reaction"
                          />
                        )}
                      </div>
                    </div>
                    <div className="matches-mobile-side-actions">
                      {showMatchMade ? (
                        <Button
                          variant="contained"
                          className="matches-select-match matches-select-match--inline matches-select-match--made matches-mobile-counterpart-action"
                          disabled
                        >
                          Match Made
                        </Button>
                      ) : showSelectPerfectMatch ? (
                        <Button
                          variant="outlined"
                          className="matches-select-match matches-select-match--inline matches-mobile-counterpart-action"
                          disabled={isSelecting}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectPerfectMatch(match);
                          }}
                        >
                          {isSelecting ? "Selecting..." : "Select as Perfect Match"}
                        </Button>
                      ) : null}
                      <div className="matches-mobile-score">
                        <MatchScoreRing value={match.matchScore} size="small" />
                      </div>
                    </div>
                  </div>
                  <div className="matches-mobile-tags">
                    <span className="matches-mobile-tags-label">Top Matching Areas</span>
                    <div className="matches-chip-row">
                      {topTags.length ? (
                        topTags.slice(0, 3).map((tag) => (
                          <span key={tag} className="matches-chip">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="matches-chip matches-chip-muted">No tags yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {endOfListMessage && mobileIndex === matches.length - 1 && !hasNextPage && (
        <div className="matches-mobile-end">{endOfListMessage}</div>
      )}
    </div>
  );
};
